
“Trickle-down teaching” is dead; long live HQIM (high-quality instructional materials).

I recently took a stab at [this paper](https://d3e7x39d4i7wbe.cloudfront.net/uploads/pdf/file/15/K12_Entrepreneurship__-1523562447.pdf) written by Larry Berger and David Stevenson in October 2007.

What big pieces emerged for me as we now enter 2020 onwards?

*How does Amplify avoid a "this too shall pass"?*

> "we couldn’t make a thick, difficult product that required in-depth, one-on-one training"

Very interesting how the term "thick" has now bled out into our nomenclature dealing with IMS Global Learning Consortium Common Cartridges for Chicago Public School Systems. Thin vs thick instructional materials?

# latest news

- [Amplify announces integration of goal-setting product, Huddle, into its line of curriculum products - Amplify](https://amplify.com/news/amplify-announces-integration-of-goal-setting-product-huddle-into-its-line-of-curriculum-products/)

# Notes from [Better Procurement Processes with Guest Larry Berger](https://hubhopper.com/episode/better-procurement-processes-with-guest-larry-berger-1579407813)

- a lot of currency with policy people
- foundations read it

# Notes from "K-12 Entrepreneurship: Slow Entry, Distant Exit" by Larry Berger and David Stevenson

> "We play this game because, as educational entrepreneurs, we are desperate
for scale. We operate in a $500 billion market, but the dollars are scattered
among 15,000 school districts, few of which are large enough to see from
mid-air. We also play this game to distract ourselves from the thought
that, while we are flying over these districts, the vast sales forces of the
big publishers are already on the ground in them, meeting the Deputy
Superintendent at the Rotary Club, luring away the last few discretionary
dollars she has."

- tool builder vs school builder
- "surprised to learn that their philanthropy can be counterproductive for our type of educational entrepreneur"
- how do you invoke the same humanity/empathy a school builder tends to attract?
- let the teacher teach; let the student learn; build a tool in a way that "users/customers" beg for it to be there
- barriers
  - Barrier #1: The Education Sector Does Not Invest in Innovation
    - 1/100: Chris Whittle has noted that the annual federal investment in healthcare R&D outstrips that of education by a ratio of 100 to 1: The National Institutes of Health’s annual budget is $27 billion versus an “unpacked” Institute of Education Sciences R&D budget of $260 million
    - 3.5: Keeping the lights on and a teacher in every classroom consumes most of the annual money spent on education, so that little is left over to generate or try new tools, techniques, or approaches. Out of every dollar spent on education in 2005, only 3.5 cents was spent on materials, tools, and services
    - 1.8: school system spends roughly 1.8 percent of its annual operating budget on information technology (IT)
  - Barriers #2 and #3: Oligopoly + Decentralization
    - big three: Pearson, McGraw-Hill, and Riverdeep/Houghton-Mifflin
    - core, or “basal” textbook purchases towards “supplemental” or “intervention” materials
    - decentralized demand in the sector. With 50 State Education Agencies, 16,000 districts, intermediate units in most states, and 65,000 schools, there are a lot of decision-makers
  - Barrier #4: Vicious Sales Cycles
    - “From the complexity of the district decision-making process you just described, it seems that in education, no one is in charge.”
    - In education, everyone is in charge.
  - Barrier #5: Pilot Error
    -  Many promising start-up companies have been killed by early interest in their product from people who were not quite ready to purchase it at a scale that is economically viable. So the start-up company agrees to do a pilot program at a low price to get in the door but ends up servicing the customer with a lot of expensive onsite handholding because the stakes are high that the pilot be successful
    - “this too shall pass” posture toward “just another pilot.”
    - "we couldn’t make a thick, difficult product that required in-depth, one-on-one training"
  - Barrier #6: No Return
    - "The problem for education ventures is that such administrators will tend to make decisions within their comfort zone – they will usually choose to solve a problem with additional district people and processes rather than with tools, systems, or outsourced resources – without regard to whether the additional district people might be the more expensive or less effective option. The return on investment mindset that drives other sectors to replace expensive labor with technology, and that sees the logic of scaling such efficiencies rapidly, does not come naturally to K-12."
    - "access to attendance data, formative assessment data and basic electronic communication and research tools that are now taken for granted in other professions and in other school districts"
  - Barrier #7: Viewing Teacher Time as a Sunk Cost
    - Teacher time is viewed as a sunk cost and therefore saving teacher time does not “count” in economic terms. Sometimes districts must reduce their headcount, but never, in our experience, because of an assumption about increased productivity.
  - Barrier #8: Short-Lived Superintendents
    - tenure of superintendents in large districts, while not as brief as has been popularly reported, averages fewer than five years
  - Barrier #9: The Vendor Wall
    - > I wish you the very best in your new venture. It sounds very exciting and certainly a key area of expansion at the present time. At the state department, we have a policy that recommends that we not meet with vendors. This protects us but also protects you. If we were to issue an RFP and you had met with us prior to the issuance, it might be construed by others as your having unfair advantage in the RFP process.
    - There is no one that we know of, in any district, whose primary role is to be on top of the latest innovations or to be an expert at managing relationships with outside providers. 
    - “There is one basic principle of success in education: The School Man doesn’t want to be embarrassed.”
    - There are no “business development” people in education, and this sort of close partnership is rare indeed. Only twice in more than a thousand district sales have we been asked what type of relationship, or what size of contract, would help our company succeed.
  - Barrier #10: Start-Up Capital
    - But for stock options to have value, there has to be a “liquidity event” on the horizon – a sale or an initial public offering that converts shares to dollars. Those experienced executives know that angel investors often signal a company that will take its time getting to a liquidity event, so they prefer venture capital-funded companies, of which there are few in education
  - Barrier #11: Free Things
    - “educational data management” and “formative assessment” are priorities
- Removing the Barriers
  1. Achieve Scale and Collaboration by Forming Consortia
  2. Commissioning R&D Rather Than Procuring Finished Products
  3. Create a Welcoming Climate for Promising Disruptions, Including Open Source Business Models
  4. Products Plus Services
  5. Tool Building for School Building

# [Twelve Years Later: What’s Really Changed in the K-12 Sector?](https://www.edsurge.com/news/2019-04-03-twelve-years-later-what-s-really-changed-in-the-k-12-sector-part-1)

- Amplify offers two English Language Arts programs for grades K-5 and 6-8, and a K-8 science curriculum. Our mCLASS assessments for K-3 reading are used by more than 2 million students across the country.
- “Thanks! Interesting how times have changed in 15 short years. Then, they needed new tech solutions to save teacher time. Now we need solutions to declutter teachers from the new tech solutions.”
- When it comes to instruction, the work consists of four segments: core curriculum, supplemental (intervention, test prep, little books) curriculum, assessment, and technology (hardware, infrastructure and connectivity).
- product lines evolve over time to integrate with one another. Most have done some work on the issue, but rarely at depth. It’s a catch-22: so long as buying is fragmented, it’s hard to justify the integrated product investment; so long as products are fragmented, it’s hard for a district to create an integrated instructional model.
- [Pareto distribution - Wikipedia](https://en.wikipedia.org/wiki/Pareto_distribution)

# A Brief History of Education

- history of how to make schools better?
- curriculum is not at the center?
- ed-week today deputy secretary of education
- decade was teacher -> school governance -> HQIM
- public education 18th century
  - have public education (whoever could afford)
- public edu 19th century
  - taxes to fund
- fix laws 1950s legal problem
- beat Russians at curriculum 1959
  - American defense and education act
  - if you wanted billion of funding to make curriculum there was affidavit to sign that you didn't believe in overthrow of US government
- fix poverty 1960s civil rights as well
- fund public education 1965
- fix management 1970
- fix expectations 1990s subjects and grades (Nation at Risk)
- fix governance 1990s (charters and choice)
  - nobody is defunding education
  - where does the money go then?
  - more in absolute items but definitely increase in line items
- have standards 1990s (goals 2000)
- have accountability 2002 no child left behind
- fix teachers 2010
  - incentives
  - trickle down teaching? Bill Gates
- fix standards and accountability 2010
- fix instructional materials 2015
  - testing? no future for it
  - how are we teaching?
- HQIM 2019!!!! high quality instructional materials
  - Ed Reports (meet standards)
  - start with the curriculum to train with the material and then grow from there (DERIVATION) start small and get big
  - write assessments about what the kids know
  - Amplify as an organization is growing here but the foundation is all these things
  - we're enabled now in 2015 to not have a silver bullet but to think about the what and the how and the why

# Trickle Down teaching explained

Gates Foundation education
- [Bill Gates Chats with Ellen for the First Time](https://youtu.be/f5NJQiY9AuY?t=257)
- phenomenal teachers
  - take top 10% and empower them to spread ideas all across the country

# Hot takes

- [Twelve Years Later: How the K-12 Industry and Investment Landscape Has Shifted (Part 2) | EdSurge News](https://www.edsurge.com/news/2019-04-05-twelve-years-later-how-the-k-12-industry-and-investment-landscape-has-shifted-part-2)
  - "The “oligopoly,” as we called it, was the natural outcome of a highly decentralized system and fragmented demand. To serve 15,000-plus districts and more than 100,000 school buildings, a company needed huge sales and service teams; to afford them, the company needed a bookbag full of products across content areas, grade ranges, and use cases. The structure of demand created the “Big Three”—McGraw-Hill, Houghton Mifflin Harcourt and Pearson."
  - "To serve 15,000-plus districts and more than 100,000 school buildings, a company needed huge sales and service teams; to afford them, the company needed a bookbag full of products across content areas, grade ranges, and use cases"
  - 10-12 years ago
    - Amplify
    - Curriculum Associates
    - iXL
    - Renaissance Learning
    - others
  - core, supplemental, and assessment
  - $6 billion instructional materials market
  - assessment (including state tests) is another $1.2 billion or so
  - several years ago, two of the “Big Three” integrated their supplemental and core sales forces
  - "eyeballs-first" vs "platform" vs "policy responsive"
  - ![](https://edsurge.imgix.net/uploads/photo/image/6765/dave-1554361445.png?w=1296&h=972&auto=compress%2Cformat&fit=crop)
  - "The stuff—software logins, workbooks, kits—continues to pile up. That hero teacher is still in charge of synthesizing it all."
- [At CAGR 28.7 % Online K-12 Education Market to Hit US$ 305900 million by 2024, from US$ 67300 million in 2019 - MarketWatch](https://www.marketwatch.com/press-release/at-cagr-287-online-k-12-education-market-to-hit-us-305900-million-by-2024-from-us-67300-million-in-2019-2019-02-05)
  - "According to this study, over the next five years the Online K-12 Education market will register a 28.7% CAGR in terms of revenue, the global market size will reach US$ 305900 million by 2024, from US$ 67300 million in 2019. In particular, this report presents the global revenue market share of key companies in Online K-12 Education business, shared in Chapter 3."
- [Publishing for the PreK-12 Market, 2019-2020 Sample.pdf](chrome-extension://mhjfbmdgcfjbbpaeojofohoefgiehjai/index.html)
- [We Can’t Just Invest in Building Great Curricula - Education Next : Education Next](https://www.educationnext.org/cant-just-invest-building-great-curricula/)
  - According to EdReports, thirteen products have met its high standards for alignment and usability in elementary or middle school English language arts; ten in math. These include programs by nonprofit upstarts, like Great Minds’ Wit and Wisdom and Eureka Math K-5, EL Education Language Arts, and Open Up Resources Math, authored by Illustrative Mathematics, and as well as offerings from for-profits and legacy publishers, including American Reading Company’s ARC Core, Pearson’s ReadyGen and MyPerspectives, and McGraw-Hill’s StudySync.
- [EdReports | Compare Materials](https://www.edreports.org/compare/results/science-68)
- [EdReports | The State of the Instructional Materials Market - 2018 Report](https://www.edreports.org/impact/state-of-the-market)
  - The U.S. K-12 curriculum market is an $8 billion a year industry, which receives a large portion of its revenue from the public sector. Yet very little is known about curriculum adoption and procurement and even less is known about the materials in use in classrooms. 
  - [the-state-of-instructional-materials-market-report_final_aug28.pdf](chrome-extension://mhjfbmdgcfjbbpaeojofohoefgiehjai/index.html)
  - [Executive Summary.pdf](chrome-extension://mhjfbmdgcfjbbpaeojofohoefgiehjai/index.html)
  - TNTP and EdTrust

# Companies with skin in the game

- Pearson (Nexus Capital Management)
- Illuminate Education (Insight Venture Partners)
  - Illuminate Education
  - Key Data Systems (KDS)
  - IO Education
  - SchoolCity
  - Alpine Achievement
- Weld North
  - Edgenuity
  - Generation Ready
  - Imagine Learning

# Relevant news

- [Finally: Pearson Sells Its US K-12 Courseware Business—for $250 Million* | EdSurge News](https://www.edsurge.com/news/2019-02-18-finally-pearson-sells-its-us-k-12-courseware-business)
  - "Nearly a year after announcing the plan, Pearson has finally sold its U.S K-12 courseware business. The buyer: Nexus Capital Management, a Los Angeles-based private equity firm."
- [K-12 Ed-Tech Platform and Tools Market Value to Increase to $1.83 Billion by 2020, Report Says - Market Brief](https://marketbrief.edweek.org/marketplace-k-12/k-12-ed-tech-platform-tools-market-value-increase-1-83-billion-2020-report-says/)
- [What's Trending in New Ed-Tech 'Top 40' Digital Tools - Market Brief](https://marketbrief.edweek.org/marketplace-k-12/whats-trending-new-ed-tech-top-40-digital-tools/)
- [Highly Rated Curricula Are Not the Norm in More Than a Third of Large Districts Studied.](https://marketbrief.edweek.org/marketplace-k-12/highly-rated-curricula-not-norm-third-large-districts-studied/)

# Resources

- [What subjects does the American K12 curriculum consist of? - Quora](https://www.quora.com/What-subjects-does-the-American-K12-curriculum-consist-of)
  - kindergarten thru 9th
    - math
    - English
    - history
    - science
  - 9th thru 12th
    - required
      - maths
      - English
      - social science and history
      - science
      - athletics
      - foreign Language (at least 2 years)
    - optional
      - Architecture
      - Art
      - Music
      - IT
      - Digital Design
      - Economics
      - Team Sports
    - other obscure
      - African History
      - Wumbology (at least one highschool has indeed taught this)
      - Philosophy
      - Woodworking
- [2019 Annual Report Our nation, in numbers.](chrome-extension://mhjfbmdgcfjbbpaeojofohoefgiehjai/index.html)
- [Download the 2019 Annual Report - USAFacts](https://annualreport.usafacts.org/articles/52-download-2019-annual-report)
- [United States Governments 10-K 2019](https://usafacts.org/reports/2019/government-10-k)
- [Classroom Slides Downloads](https://looker-data.prod.learning.amplify.com/dashboards/653)
- [Yesterday Science Apps](https://looker-data.prod.learning.amplify.com/dashboards/695)
- [AllScienceAppsLocations](https://looker-data.prod.learning.amplify.com/dashboards/694)
- [Looker Dashboards](https://impact.amplify.com/science/looker/)
- https://docs.google.com/spreadsheets/d/1wrj_RPHHwQ6NHmw10FFegDQy58u0dcoGejmA6whIIr0/edit#gid=539376702
- [Amplify_Science_Research_GK-8-1.pdf](chrome-extension://mhjfbmdgcfjbbpaeojofohoefgiehjai/index.html)
- research: https://drive.google.com/file/d/1ehP71qHL8lwbhqium_daktAoV2vabQvU/view
- NSF funding: https://drive.google.com/file/d/1rLv0vIytBEK9gdWUd4BzyXJhturGpHPI/view
- [Amplify Science](https://impact.amplify.com/science/)
